import * as React from "react";
import { Alert, Button, Card, Modal, Popconfirm, Space, Tag } from "antd";
import { CloseOutlined, EditOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { Table } from "../../newcomponents/table/table";
import { CardTitle } from "../../components/antd/card/CardTitle";
import { CardContent } from "../../components/antd/card/CardContent";
import { IdentityInput } from "../../schema/types/identity/input";
import { v4 as uuidv4 } from "uuid";
import { slug } from "github-slugger";
import { formSet, formSetValue, formTriggerValidation, useCpFormValue } from "../gvc/testNewForm/utils";
import { useCpFormContext } from "../../components/form/useCpFormContext";
import { store } from "../../store";
import { cloneDeep } from "lodash";
import { CpInput } from "../gvc/testNewForm/CpInput";
import { CpFormItem } from "../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../gvc/testNewForm/CpLabel";
import { CpErrorWarning } from "../gvc/testNewForm/CpErrorWarning";
import { CpArrayString } from "../gvc/testNewForm/CpArrayString";
import { getDefaultKindSelectState } from "../../components/antd/kindselect/KindSelect";
import { CpKindSelect } from "../gvc/testNewForm/temp/CpKindSelect";

export const IdentityCreateNetworkResourceFQDN: React.FC = () => {
  const { id } = useCpFormContext();

  const isAddingItem = useCpFormValue(`networkResources.fqdnItemForm.isAddingItem`);
  const setIsAddingItem = (value: string | boolean) => {
    formSetValue(id, `networkResources.fqdnItemForm.isAddingItem`, value);
  };

  const fqdnResources = useCpFormValue(`networkResources.fqdn`);
  let filteredFqdnResources = fqdnResources;
  filteredFqdnResources = filteredFqdnResources.filter((r) => {
    if (isAddingItem !== true && isAddingItem !== false) {
      return r.id !== isAddingItem;
    }
    return true;
  });
  const ipResources = useCpFormValue(`networkResources.ip`);
  const awsResources = useCpFormValue(`nativeNetworkResources.aws`);
  const gcpResources = useCpFormValue(`nativeNetworkResources.gcp`);

  const allResourcesFiltered = [...filteredFqdnResources, ...ipResources, ...awsResources, ...gcpResources];
  const allFqdns = React.useMemo(() => allResourcesFiltered.map((r) => (r as any).fqdn).filter(Boolean), [
    allResourcesFiltered,
  ]) as string[];
  const allNames = React.useMemo(() => allResourcesFiltered.map((r) => r.name).filter(Boolean), [
    allResourcesFiltered,
  ]) as string[];

  const onDoneItemForm = async () => {
    const form = store.getState().form.instances[id];
    const values: IdentityInput = form.values as any;

    const itemForm = cloneDeep(values.networkResources.fqdnItemForm);

    if (!itemForm.name) {
      itemForm.name = itemForm.generatedName;
    }
    let currentFqdnArray = cloneDeep(values.networkResources.fqdn);
    if (typeof isAddingItem !== "boolean") {
      currentFqdnArray = currentFqdnArray.filter((r) => r.id !== isAddingItem);
    }

    currentFqdnArray.push({
      id: uuidv4(),
      fqdn: itemForm.fqdn,
      name: itemForm.name,
      agent: itemForm.agent,
      resolverIp: itemForm.resolverIp,
      ports: itemForm.ports.map((p) => p.value),
    });

    formSetValue(id, `networkResources.fqdn`, currentFqdnArray);
    formSet(id, `fieldStates.networkResources.fqdnItemForm.fqdn.simulateTouched`, false);
    formSet(id, `fieldStates.networkResources.fqdnItemForm.name.simulateTouched`, false);
    formSet(id, `fieldStates.networkResources.fqdnItemForm.agent.simulateTouched`, false);
    formSet(id, `fieldStates.networkResources.fqdnItemForm.portsroot.simulateTouched`, false);
    formTriggerValidation(id);
    setIsAddingItem(false);
  };

  const onRemoveItem = async (index: number) => {
    const form = store.getState().form.instances[id];
    const values: IdentityInput = form.values as any;

    const currentFqdnArray = cloneDeep(values.networkResources.fqdn);
    currentFqdnArray.splice(index, 1);
    formSetValue(id, `networkResources.fqdn`, currentFqdnArray);
    formTriggerValidation(id);
  };

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardTitle>FQDN Resources</CardTitle>
          <CardContent>
            {fqdnResources.length > 0 ? (
              <Table<any>
                tableId="fqdnResourcesEditable-update-3"
                data={fqdnResources}
                hideSettings
                columns={[
                  { id: "name", label: "Name", enableResize: true, size: 200 },
                  { id: "agent.value", label: "Agent", enableResize: true, size: 200 },
                  {
                    id: "ports",
                    label: "Ports",
                    enableResize: true,
                    size: 200,
                    cell: ({ row }) => {
                      const ports = row.original.ports || [];
                      return (
                        <div className="flex items-center">
                          {ports.map((port) => (
                            <Tag key={port}>{port}</Tag>
                          ))}
                        </div>
                      );
                    },
                  },
                  { id: "fqdn", label: "FQDN", enableResize: true, size: 200 },
                  { id: "resolverIp", label: "Resolver IP", enableResize: false, size: 200 },
                  {
                    id: "action",
                    label: "",
                    enableResize: false,
                    size: 120,
                    cell: ({ row }) => {
                      return (
                        <div className="w-full flex justify-end">
                          <Space.Compact>
                            <Button
                              htmlType={`button`}
                              onClick={() => {
                                const item = fqdnResources.find((r) => r.id === row.original.id);
                                if (item) {
                                  formSetValue(id, `networkResources.fqdnItemForm`, {
                                    isAddingItem: row.original.id,
                                    agent: item.agent,
                                    fqdn: item.fqdn,
                                    name: item.name,
                                    generatedName: item.name,
                                    resolverIp: item.resolverIp,
                                    ports: item.ports.map((p) => ({ id: uuidv4(), value: p })),
                                  });
                                }
                              }}
                              type={"text"}
                              icon={<EditOutlined />}
                            />
                            <Popconfirm title={"Confirm"} onConfirm={() => onRemoveItem(row.index)}>
                              <Button htmlType={`button`} type={"text"} icon={<CloseOutlined />} danger />
                            </Popconfirm>
                          </Space.Compact>
                        </div>
                      );
                    },
                  },
                ]}
              />
            ) : null}
            {!isAddingItem ? null : (
              <ItemFormContent
                isAddingItem={isAddingItem}
                allFqdns={allFqdns}
                allNames={allNames}
                onCancel={() => {
                  // Clear simulateTouched states
                  formSet(id, `fieldStates.networkResources.fqdnItemForm.fqdn.simulateTouched`, false);
                  formSet(id, `fieldStates.networkResources.fqdnItemForm.name.simulateTouched`, false);
                  formSet(id, `fieldStates.networkResources.fqdnItemForm.agent.simulateTouched`, false);
                  formSet(id, `fieldStates.networkResources.fqdnItemForm.portsroot.simulateTouched`, false);
                  formSetValue(id, `networkResources.fqdnItemForm`, {
                    isAddingItem: false,
                    fqdn: "",
                    name: "",
                    generatedName: "",
                    agent: getDefaultKindSelectState(),
                    resolverIp: "",
                    ports: [],
                  });
                }}
                onDone={onDoneItemForm}
              />
            )}
            <Button
              htmlType={`button`}
              className="mt-4"
              type={"dashed"}
              icon={<PlusCircleOutlined />}
              onClick={() => {
                formSetValue(id, `networkResources.fqdnItemForm`, {
                  isAddingItem: true,
                  fqdn: "",
                  name: "",
                  generatedName: "",
                  agent: getDefaultKindSelectState(),
                  resolverIp: "",
                  ports: [],
                });
              }}
            >
              Add FQDN
            </Button>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

interface Props {
  isAddingItem: boolean | string;
  allFqdns: string[];
  allNames: string[];
  onCancel: any;
  onDone: any;
}
const ItemFormContent: React.FC<Props> = ({ isAddingItem, allFqdns, allNames, onCancel, onDone }) => {
  const { id } = useCpFormContext();

  const fqdn = useCpFormValue(`networkResources.fqdnItemForm.fqdn`);
  React.useEffect(() => {
    const generatedName = slug(fqdn);
    formSetValue(id, `networkResources.fqdnItemForm.generatedName`, generatedName);
  }, [fqdn]);

  const name = useCpFormValue(`networkResources.fqdnItemForm.name`);
  const generatedName = useCpFormValue(`networkResources.fqdnItemForm.generatedName`);

  const agentValue = useCpFormValue(`networkResources.fqdnItemForm.agent.value`);

  const ports = useCpFormValue(`networkResources.fqdnItemForm.ports`);

  const isFqdnUnique = !allFqdns.includes(fqdn);
  const nameToCheck = name || generatedName;
  const isNameUnique = !allNames.includes(nameToCheck);

  const isValid =
    fqdn &&
    isFqdnUnique &&
    nameToCheck &&
    isNameUnique &&
    agentValue &&
    ports.length > 0 &&
    !ports.some((p) => !p.value);

  return (
    <Modal
      open={isAddingItem !== false}
      title={`${isAddingItem === true ? "Add" : "Edit"} FQDN Resource`}
      okText={isAddingItem === true ? "Add" : "Update"}
      cancelText="Cancel"
      okButtonProps={{ htmlType: "submit" }}
      onCancel={onCancel}
      onOk={() => {
        // Mark all fields as simulateTouched so error states are shown
        formSet(id, `fieldStates.networkResources.fqdnItemForm.fqdn.simulateTouched`, true);
        formSet(id, `fieldStates.networkResources.fqdnItemForm.name.simulateTouched`, true);
        formSet(id, `fieldStates.networkResources.fqdnItemForm.agent.simulateTouched`, true);
        formSet(id, `fieldStates.networkResources.fqdnItemForm.portsroot.simulateTouched`, true);
        formTriggerValidation(id);
        if (!isValid) {
          return;
        }
        onDone();
      }}
      destroyOnHidden
      closable={false}
      maskClosable={false}
    >
      {isFqdnUnique ? null : <Alert className="mb-2" type={`error`} message={`All "FQDN" values must be unique`} />}
      {isNameUnique ? null : <Alert className="mb-2" type={`error`} message={`All "name" values must be unique`} />}
      <CpFormItem>
        <CpLabel name={`networkResources.fqdnItemForm.fqdn`}>FQDN</CpLabel>
        <CpInput name={`networkResources.fqdnItemForm.fqdn`} />
        <CpErrorWarning name={`networkResources.fqdnItemForm.fqdn`} />
      </CpFormItem>
      <CpFormItem>
        <CpLabel name={`networkResources.fqdnItemForm.name`}>Name</CpLabel>
        <CpInput name={`networkResources.fqdnItemForm.name`} placeholder={generatedName} />
        <CpErrorWarning name={`networkResources.fqdnItemForm.name`} />
      </CpFormItem>
      <CpFormItem>
        <CpLabel name={`networkResources.fqdnItemForm.agent`}>Agent</CpLabel>
        <CpKindSelect kind="agent" optionValue="name" name={`networkResources.fqdnItemForm.agent`} />
        <CpErrorWarning name={`networkResources.fqdnItemForm.agent`} />
      </CpFormItem>
      <CpFormItem>
        <CpLabel name={`networkResources.fqdnItemForm.resolverIp`}>Resolver IP</CpLabel>
        <CpInput name={`networkResources.fqdnItemForm.resolverIp`} />
        <CpErrorWarning name={`networkResources.fqdnItemForm.resolverIp`} />
      </CpFormItem>
      <CpArrayString label="Ports" name={`networkResources.fqdnItemForm.ports`} />
    </Modal>
  );
};
